import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Camera, Globe, Mail, X, ArrowRight } from "lucide-react";
import Logo from "@/components/Logo";

const STORAGE_KEY = "ba_onboarding_done_v1";

const STEPS = [
  {
    key: "capture",
    icon: Camera,
    title: "Foto oder PDF",
    text: "Fotografiere deinen Brief oder lade ein PDF hoch. Auch mehrseitige Briefe funktionieren – lade einfach mehrere Fotos hintereinander hoch.",
    accent: "bg-blue-600",
  },
  {
    key: "understand",
    icon: Globe,
    title: "Verstehen in deiner Sprache",
    text: "BriefAssist erkennt Absender, Frist, Betrag und Dringlichkeit – und erklärt dir den Brief in einfacher Alltagssprache in 12 Sprachen.",
    accent: "bg-amber-500",
  },
  {
    key: "act",
    icon: Mail,
    title: "Antworten & Fristen speichern",
    text: "Erstelle mit einem Tipp eine professionelle Antwort auf Deutsch, speichere Fristen im Kalender oder teile den Brief mit deiner Familie.",
    accent: "bg-emerald-500",
  },
];

export default function OnboardingTour() {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    try {
      const done = localStorage.getItem(STORAGE_KEY);
      if (!done) {
        // Small delay so home screen renders first
        const t = setTimeout(() => setOpen(true), 350);
        return () => clearTimeout(t);
      }
    } catch (_) { void 0; }
  }, []);

  const finish = () => {
    try { localStorage.setItem(STORAGE_KEY, "1"); } catch (_) { void 0; }
    setOpen(false);
  };

  if (!open) return null;

  const s = STEPS[step];
  const Icon = s.icon;
  const isLast = step === STEPS.length - 1;

  return (
    <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4"
         data-testid="onboarding-overlay" role="dialog" aria-modal="true">
      <div className="w-full max-w-[480px] bg-white rounded-3xl shadow-2xl overflow-hidden animate-in slide-in-from-bottom-6 fade-in duration-300">
        <div className="flex items-center justify-between px-5 pt-4">
          <Logo />
          <button
            onClick={finish}
            data-testid="onboarding-skip"
            aria-label="Überspringen"
            className="p-2 rounded-full hover:bg-slate-100"
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        <div className="px-5 pt-4 pb-6">
          <div className={`w-16 h-16 rounded-2xl ${s.accent} text-white flex items-center justify-center shadow-lg mb-4`}>
            <Icon className="w-8 h-8" strokeWidth={2.2} />
          </div>
          <h2 className="text-2xl font-bold text-slate-900" data-testid={`onboarding-title-${s.key}`}>{s.title}</h2>
          <p className="mt-2 text-slate-600 text-[15px] leading-relaxed">{s.text}</p>

          <div className="flex items-center gap-2 mt-6" data-testid="onboarding-dots">
            {STEPS.map((_, i) => (
              <span key={i} className={`h-1.5 rounded-full transition-all ${i === step ? "w-6 bg-blue-600" : "w-1.5 bg-slate-200"}`} />
            ))}
          </div>

          <div className="mt-6 flex items-center gap-3">
            {step > 0 && (
              <button
                onClick={() => setStep(s => Math.max(0, s - 1))}
                data-testid="onboarding-back"
                className="px-4 h-12 rounded-2xl bg-slate-100 text-slate-700 font-semibold"
              >Zurück</button>
            )}
            {!isLast ? (
              <button
                onClick={() => setStep(s => Math.min(STEPS.length - 1, s + 1))}
                data-testid="onboarding-next"
                className="flex-1 h-12 rounded-2xl bg-blue-600 text-white font-semibold flex items-center justify-center gap-2 shadow-md shadow-blue-600/20"
              >
                Weiter <ArrowRight className="w-5 h-5" />
              </button>
            ) : (
              <button
                onClick={() => { finish(); navigate("/upload"); }}
                data-testid="onboarding-finish"
                className="flex-1 h-12 rounded-2xl bg-blue-600 text-white font-semibold flex items-center justify-center gap-2 shadow-md shadow-blue-600/20"
              >
                Los geht&apos;s <Camera className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
